import React, { useState } from 'react';
import { PAGE_STATUS, btnGhost, btnPrimary, card, overlay } from './styles';

/**
 * One chip per catalog page, coloured by what happened to it. Clicking a chip shows the page
 * (fetched fresh — page links are short-lived) with its error, and lets the reviewer re-read it.
 */
export default function PageStrip({ pages, running, loadPages, onReread }) {
  const [open, setOpen] = useState(null); // the page being viewed
  const [busy, setBusy] = useState(false);

  const view = async (pageNumber) => {
    setBusy(true);
    try {
      const fresh = await loadPages();
      setOpen(fresh.find((p) => p.pageNumber === pageNumber) || null);
    } finally {
      setBusy(false);
    }
  };

  const reread = async () => {
    const ok = await onReread(open.pageNumber);
    if (ok) setOpen(null);
  };

  if (pages.length === 0) return null;
  const counts = pages.reduce((acc, p) => ({ ...acc, [p.status]: (acc[p.status] || 0) + 1 }), {});

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#6B7280', marginBottom: 8 }}>
        {Object.entries(PAGE_STATUS).filter(([k]) => counts[k]).map(([k, s]) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: s.bg, border: `1.5px solid ${s.fg}` }} />
            {s.text}: {counts[k]}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {pages.map((p) => {
          const s = PAGE_STATUS[p.status] || PAGE_STATUS.PENDING;
          return (
            <button key={p.pageNumber} disabled={busy} onClick={() => view(p.pageNumber)}
                    title={`Page ${p.pageNumber}: ${s.text}`} aria-label={`Page ${p.pageNumber}, ${s.text}`}
                    style={{ minWidth: 34, height: 30, padding: '0 6px', borderRadius: 6, border: `1.5px solid ${s.fg}`, background: s.bg, color: s.fg, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              {p.pageNumber}
            </button>
          );
        })}
      </div>

      {open && (
        <div style={overlay} role="dialog" aria-modal="true" aria-label={`Catalog page ${open.pageNumber}`}>
          <div style={{ ...card, width: 'min(1000px, 100%)', maxHeight: '94vh', overflowY: 'auto', marginBottom: 0 }}>
            <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700 }}>
              Page {open.pageNumber} — {(PAGE_STATUS[open.status] || PAGE_STATUS.PENDING).text}
            </h3>
            {open.errorMessage && <p style={{ color: '#B91C1C', fontSize: 13, margin: '0 0 10px' }}>{open.errorMessage}</p>}
            {open.reused && <p style={{ color: '#166534', fontSize: 12, margin: '0 0 10px' }}>Identical to a page read before — its reading was reused at no AI cost.</p>}
            <div style={{ background: '#F3F4F6', borderRadius: 8, padding: 8, textAlign: 'center' }}>
              <img src={open.renderUrl} alt={`Catalog page ${open.pageNumber}`} style={{ maxWidth: '100%', maxHeight: '68vh' }} />
            </div>
            {open.status === 'SKIPPED' ? (
              <p style={{ fontSize: 12, color: '#6B7280', margin: '10px 0 0' }}>This page was skipped — reading it now sends it to AI (about ₹2–3).</p>
            ) : (
              <p style={{ fontSize: 12, color: '#6B7280', margin: '10px 0 0' }}>
                Re-reading replaces this page's drafts that haven't been created or discarded yet — including any edits to them.
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button style={btnGhost} onClick={() => setOpen(null)}>Close</button>
              <button style={{ ...btnPrimary, opacity: running ? 0.6 : 1 }} disabled={running} onClick={reread}
                      title={running ? 'Wait for the current run to finish' : undefined}>
                {open.status === 'SKIPPED' ? 'Read this page' : 'Re-read this page'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
